import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ClipboardList, Clock, GripVertical, Search, X } from 'lucide-react';
import type { FacilityFlowCoreModulesPlanningDtosDueTasksDueTaskDto } from '../../../api/generated/models';
import { getObjectColors } from '../../../utils/objectColors';
import { formatDate } from '../../../utils/dateUtils';

export const DUE_TASK_DRAG_TYPE = 'application/due-task';

type DueTask = FacilityFlowCoreModulesPlanningDtosDueTasksDueTaskDto;

export interface DueTaskDragData {
  taskId: string;
  name: string;
  estimatedDurationMinutes: number;
}

interface DueTasksSidepanelProps {
  dueTasks: DueTask[];
  loading: boolean;
  year: number;
  week: number;
  /** ISO date (yyyy-MM-dd). When set, the panel narrows to tasks plannable on that day. */
  date?: string;
  onTaskClick?: (taskId: string) => void;
}

type ScheduleFilter = 'day' | 'week' | 'month' | 'none';

interface ObjectGroup {
  key: string;
  name: string;
  customerName: string | null;
  tasks: DueTask[];
}
type ViewMode = 'grouped' | 'list';

const SCHEDULE_FILTERS: ScheduleFilter[] = ['day', 'week', 'month', 'none'];
const VIEW_MODE_STORAGE_KEY = 'ff.planning.dueTasks.view';

function loadViewMode(): ViewMode {
  try {
    return localStorage.getItem(VIEW_MODE_STORAGE_KEY) === 'list' ? 'list' : 'grouped';
  } catch {
    return 'grouped';
  }
}

function saveViewMode(mode: ViewMode) {
  try {
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
  } catch {
    // Storage unavailable (private mode, blocked site data): view stays per-session.
  }
}

/**
 * Whether a due task can be planned on `isoDate`. Tasks without a schedule rule are
 * offered every day; scheduled ones either fire on that exact date or carry a day-free
 * week/month occurrence that may land on any day of the period.
 */
function isDueOn(task: DueTask, isoDate: string): boolean {
  if (!task.hasSchedule) return true;
  if ((task.openDates ?? []).includes(isoDate)) return true;
  if (task.weekOpen) return true;
  return (task.openMonths ?? []).includes(Number(isoDate.slice(5, 7)));
}

function matchesScheduleFilter(task: DueTask, filter: ScheduleFilter): boolean {
  switch (filter) {
    case 'day':
      return (task.openDates ?? []).length > 0;
    case 'week':
      return !!task.weekOpen;
    case 'month':
      return (task.openMonths ?? []).length > 0;
    case 'none':
      return !task.hasSchedule;
  }
}

function formatDuration(minutes: number | undefined): string {
  if (!minutes) return '–';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

export function DueTasksSidepanel({
  dueTasks,
  loading,
  year,
  week,
  date,
  onTaskClick,
}: DueTasksSidepanelProps) {
  const { t, i18n } = useTranslation(['planning', 'common']);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<Set<ScheduleFilter>>(new Set());
  const [viewMode, setViewMode] = useState<ViewMode>(loadViewMode);
  const [collapsedObjects, setCollapsedObjects] = useState<Set<string>>(new Set());

  const dayTasks = date ? dueTasks.filter((task) => isDueOn(task, date)) : dueTasks;

  const term = searchTerm.trim().toLowerCase();
  const searchedTasks = term
    ? dayTasks.filter(
        (task) =>
          (task.name || '').toLowerCase().includes(term) ||
          (task.objectName || '').toLowerCase().includes(term) ||
          (task.customerName || '').toLowerCase().includes(term),
      )
    : dayTasks;

  // Chips combine with OR: "Woche" + "Monat" shows tasks with either open occurrence.
  const filteredTasks =
    activeFilters.size === 0
      ? searchedTasks
      : searchedTasks.filter((task) => [...activeFilters].some((f) => matchesScheduleFilter(task, f)));

  const isFiltered = term.length > 0 || activeFilters.size > 0;

  const scheduledTasks = filteredTasks.filter((task) => task.hasSchedule);
  const unscheduledTasks = filteredTasks.filter((task) => !task.hasSchedule);

  const objectGroups = (() => {
    const groups = new Map<string, ObjectGroup>();
    for (const task of filteredTasks) {
      const key = task.objectId || '';
      const group = groups.get(key);
      if (group) {
        group.tasks.push(task);
      } else {
        groups.set(key, {
          key,
          name: task.objectName || t('dueTasks.noObject'),
          customerName: task.customerName ?? null,
          tasks: [task],
        });
      }
    }
    return [...groups.values()].sort((a, b) => a.name.localeCompare(b.name, i18n.language));
  })();

  const toggleFilter = (filter: ScheduleFilter) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(filter)) next.delete(filter);
      else next.add(filter);
      return next;
    });
  };

  const changeViewMode = (mode: ViewMode) => {
    setViewMode(mode);
    saveViewMode(mode);
  };

  const toggleObject = (key: string) => {
    setCollapsedObjects((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleDragStart = (e: React.DragEvent, task: DueTask) => {
    const data: DueTaskDragData = {
      taskId: task.taskId || '',
      name: task.name || '',
      estimatedDurationMinutes: task.estimatedDurationMinutes || 0,
    };
    e.dataTransfer.setData(DUE_TASK_DRAG_TYPE, JSON.stringify(data));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const formatWeekday = (isoDate: string) =>
    new Date(`${isoDate}T00:00:00`).toLocaleDateString(i18n.language, { weekday: 'short' });

  const chipClassName = `
    px-1.5 py-0.5 rounded text-[10px] font-medium
    bg-surface-sunken dark:bg-gray-800
    text-ink-muted dark:text-text-light
  `;

  const renderTask = (task: DueTask, showObject: boolean) => {
    const { color } = getObjectColors(task.objectId ?? task.taskId ?? task.name);
    const label = task.name || t('dueTasks.unnamed');
    const openDates = task.openDates ?? [];
    const openMonths = task.openMonths ?? [];
    const openCount = openDates.length + (task.weekOpen ? 1 : 0) + openMonths.length;

    return (
      <button
        type="button"
        key={task.taskId}
        draggable
        onDragStart={(e) => handleDragStart(e, task)}
        onClick={() => task.taskId && onTaskClick?.(task.taskId)}
        aria-label={label}
        className="
          relative w-full text-left pl-3 pr-2 py-1.5 rounded-md
          border border-gray-200 dark:border-gray-700
          bg-white dark:bg-gray-900
          hover:bg-surface-muted dark:hover:bg-gray-800
          focus:outline-none focus-visible:ring-2 focus-visible:ring-primary
          transition-colors group cursor-grab active:cursor-grabbing
        "
        style={{ boxShadow: `inset 2px 0 0 0 ${color}` }}
      >
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{label}</p>
            {showObject && (task.objectName || task.customerName) && (
              <p className="text-[11px] text-ink-muted dark:text-text-light truncate">
                {task.objectName}
                {task.objectName && task.customerName && ' · '}
                {task.customerName && <span className="text-ink-subtle">{task.customerName}</span>}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-1 mt-1">
              <span className="flex items-center gap-1 mr-1">
                <Clock className="w-3 h-3 text-ink-subtle" />
                <span className="text-[11px] text-ink-muted dark:text-text-light text-tabular">
                  {formatDuration(task.estimatedDurationMinutes)}
                </span>
              </span>
              {openCount > 1 && (
                <span className="text-[11px] text-ink-muted dark:text-text-light text-tabular mr-1">
                  {t('dueTasks.remaining', { count: openCount })}
                </span>
              )}
              {task.hasSchedule ? (
                <>
                  {openDates.map((openDate) => (
                    <span key={openDate} className={chipClassName}>
                      {formatWeekday(openDate)}
                    </span>
                  ))}
                  {task.weekOpen && <span className={chipClassName}>{t('dueTasks.chips.week')}</span>}
                  {openMonths.map((month) => (
                    <span key={`month-${month}`} className={chipClassName}>
                      {t(`common:scheduleRule.months.${month}`)}
                    </span>
                  ))}
                </>
              ) : (
                <span className="text-[11px] text-ink-subtle dark:text-gray-500">
                  {t('dueTasks.noSchedule')}
                </span>
              )}
            </div>
          </div>
          <GripVertical
            className="w-4 h-4 text-ink-faint dark:text-gray-600 group-hover:text-ink-muted shrink-0 mt-0.5"
            aria-hidden
          />
        </div>
      </button>
    );
  };

  const renderSection = (title: string, tasks: DueTask[]) => {
    if (tasks.length === 0) return null;

    return (
      <div>
        <p className="px-1 pt-1 pb-1.5 text-[10px] font-mono uppercase tracking-[0.18em] text-ink-subtle">
          {title}
        </p>
        <div className="space-y-1">{tasks.map((task) => renderTask(task, true))}</div>
      </div>
    );
  };

  const renderObjectGroup = (group: ObjectGroup) => {
    // Searching or filtering opens every group — a hit hidden in a collapsed group reads as "not found".
    const collapsed = !isFiltered && collapsedObjects.has(group.key);
    const { color } = getObjectColors(group.key || group.name);

    return (
      <div key={group.key}>
        <button
          type="button"
          onClick={() => toggleObject(group.key)}
          aria-expanded={!collapsed}
          className="
            w-full flex items-center gap-2 px-1 py-1 rounded-md text-left
            hover:bg-surface-muted dark:hover:bg-gray-800
            focus:outline-none focus-visible:ring-2 focus-visible:ring-primary
          "
        >
          <ChevronDown
            className={`w-3.5 h-3.5 text-ink-subtle shrink-0 transition-transform ${collapsed ? '-rotate-90' : ''}`}
            aria-hidden
          />
          <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: color }} aria-hidden />
          <span className="flex-1 min-w-0">
            <span className="block text-xs font-semibold text-gray-900 dark:text-white truncate">
              {group.name}
            </span>
            {group.customerName && (
              <span className="block text-[11px] text-ink-subtle dark:text-gray-500 truncate">
                {group.customerName}
              </span>
            )}
          </span>
          <span className="text-[11px] text-ink-subtle text-tabular">{group.tasks.length}</span>
        </button>
        {!collapsed && (
          <div className="space-y-1 mt-1 ml-1.5">{group.tasks.map((task) => renderTask(task, false))}</div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden flex flex-col h-full">
      <div className="px-3 py-3 border-b border-gray-200 dark:border-gray-700 bg-surface-muted dark:bg-gray-800">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-primary" />
          <h3 className="flex-1 font-semibold text-sm text-gray-900 dark:text-white">
            {t('dueTasks.title')}
          </h3>
          <div
            role="group"
            aria-label={t('dueTasks.view.label')}
            className="flex rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden"
          >
            {(['grouped', 'list'] as ViewMode[]).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => changeViewMode(mode)}
                aria-pressed={viewMode === mode}
                className={`px-2 py-0.5 text-[11px] font-medium transition-colors ${
                  viewMode === mode
                    ? 'bg-primary text-white'
                    : 'bg-white dark:bg-gray-900 text-ink-muted hover:bg-surface-muted dark:hover:bg-gray-800'
                }`}
              >
                {t(`dueTasks.view.${mode}`)}
              </button>
            ))}
          </div>
        </div>
        <p className="text-[11px] text-ink-muted dark:text-text-light mt-0.5 text-tabular">
          {date
            ? formatDate(`${date}T00:00:00`, {
                weekday: 'short',
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })
            : `${t('weekHeader.weekAbbr')} ${week} / ${year}`}
        </p>
      </div>

      <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 space-y-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-subtle" />
          <input
            type="text"
            placeholder={t('dueTasks.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="
              w-full pl-8 pr-8 py-1.5 text-sm
              border border-gray-200 dark:border-gray-700 rounded-md
              bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
              placeholder-ink-subtle dark:placeholder-gray-500
              focus:ring-2 focus:ring-primary focus:border-primary outline-none
            "
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              aria-label={t('dueTasks.clearSearch')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-subtle hover:text-ink-muted dark:hover:text-gray-300"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div role="group" aria-label={t('dueTasks.filters.label')} className="flex flex-wrap gap-1">
          {SCHEDULE_FILTERS.map((filter) => {
            const count = searchedTasks.filter((task) => matchesScheduleFilter(task, filter)).length;
            const active = activeFilters.has(filter);
            return (
              <button
                key={filter}
                type="button"
                onClick={() => toggleFilter(filter)}
                aria-pressed={active}
                disabled={count === 0 && !active}
                className={`
                  flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium transition-colors
                  disabled:opacity-40 disabled:cursor-not-allowed
                  ${
                    active
                      ? 'bg-primary border-primary text-white'
                      : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-ink-muted hover:bg-surface-muted dark:hover:bg-gray-800'
                  }
                `}
              >
                {t(`dueTasks.filters.${filter}`)}
                <span className="text-tabular opacity-80">{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {loading ? (
          <div className="p-4">
            <div className="animate-pulse space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-14 bg-surface-sunken dark:bg-gray-800 rounded-md" />
              ))}
            </div>
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="p-6 text-center text-ink-muted dark:text-text-light">
            <ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">
              {isFiltered
                ? t('dueTasks.noneFound')
                : t(date ? 'dueTasks.emptyDay' : 'dueTasks.empty')}
            </p>
          </div>
        ) : viewMode === 'grouped' ? (
          <div className="p-2 space-y-2">{objectGroups.map(renderObjectGroup)}</div>
        ) : (
          <div className="p-2 space-y-3">
            {renderSection(t('dueTasks.sections.scheduled'), scheduledTasks)}
            {renderSection(t('dueTasks.sections.unscheduled'), unscheduledTasks)}
          </div>
        )}
      </div>

      {dayTasks.length > 0 && (
        <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700 bg-surface-muted dark:bg-gray-800">
          <p className="text-[11px] text-ink-muted dark:text-text-light text-center text-tabular">
            {isFiltered
              ? t('dueTasks.filtered', { shown: filteredTasks.length, total: dayTasks.length })
              : t('dueTasks.count', { count: dayTasks.length })}
          </p>
        </div>
      )}
    </div>
  );
}
